import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import {
    BellRing,
    ClipboardList,
    HandCoins,
    Printer,
    UserPlus,
    Users,
    Wallet
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { useClinic } from '@/lib/clinic/store'
import { formatCompactCurrency, formatNumber, formatPercent, pluralize } from '@/lib/format'
import { useLookups, useMoney } from '@/hooks/use-clinic-helpers'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import PageHeader from '@/components/app/page-header'
import PageLoading from '@/components/app/page-loading'
import SimpleSelect from '@/components/app/simple-select'
import StatCard from '@/components/app/stat-card'
import EmptyState from '@/components/app/empty-state'
import { PaymentStatusBadge } from '@/components/app/status-badge'
import ColumnChart from '@/components/charts/column-chart'
import TrendChart from '@/components/charts/trend-chart'
import BarList from '@/components/charts/bar-list'

import {
    Card,
    CardAction,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@/components/ui/card'

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table'

import {
    addDays,
    addMonths,
    daysBetween,
    endOfMonth,
    formatDate,
    formatDateTime,
    startOfMonth
} from '@/lib/dates'

import {
    getDailyRevenue,
    getDailyVisits,
    getFollowUpCompletion,
    getInvoiceSummary,
    getMonthKeysBetween,
    getMonthlyRevenue,
    getMonthlyVisits,
    getNewVsReturning,
    getWeeklyVisits
} from '@/lib/clinic/selectors'

import { PAYMENT_METHOD_LABELS } from '@/constants/clinic'

type Preset = '7d' | '30d' | 'month' | 'last-month' | '90d' | '180d' | 'year' | 'custom'

const PRESETS: Array<{ value: Preset, label: string }> = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: 'month', label: 'This month' },
    { value: 'last-month', label: 'Last month' },
    { value: '90d', label: 'Last 3 months' },
    { value: '180d', label: 'Last 6 months' },
    { value: 'year', label: 'This year' },
    { value: 'custom', label: 'Custom range' }
]

const resolveRange = (preset: Preset, today: string, from: string, to: string): { from: string, to: string } => {
    switch (preset) {
        case '7d': return { from: addDays(today, -6), to: today }
        case '30d': return { from: addDays(today, -29), to: today }
        case 'month': return { from: startOfMonth(today), to: today }
        case 'last-month': {
            const start = startOfMonth(addMonths(startOfMonth(today), -1))
            return { from: start, to: endOfMonth(start) }
        }
        case '90d': return { from: addDays(today, -89), to: today }
        case '180d': return { from: addDays(today, -179), to: today }
        case 'year': return { from: `${today.slice(0, 4)}-01-01`, to: today }
        default: {
            const start = from || addDays(today, -29)
            const end = to || today
            return start <= end ? { from: start, to: end } : { from: end, to: start }
        }
    }
}

const headClass = 'text-xs font-semibold tracking-wide text-muted-foreground uppercase'

const ReportsPage: FC = (): ReactNode => {
    const { ready, data, today } = useClinic()
    const { patientsById } = useLookups()
    const money = useMoney()
    const currency = data.settings.billing.currency

    const [preset, setPreset] = useState<Preset>('90d')
    const [customFrom, setCustomFrom] = useState('')
    const [customTo, setCustomTo] = useState('')
    const [visitView, setVisitView] = useState<'daily' | 'weekly' | 'monthly' | 'auto'>('auto')
    const [revenueView, setRevenueView] = useState<'daily' | 'monthly' | 'auto'>('auto')

    const range = useMemo(() => resolveRange(preset, today || '2000-01-01', customFrom, customTo), [preset, today, customFrom, customTo])
    const length = daysBetween(range.from, range.to) + 1

    const report = useMemo(() => {
        const visits = data.visits.filter(item => item.date >= range.from && item.date <= range.to)
        const uniquePatients = new Set(visits.map(item => item.patientId))
        const newPatients = data.patients.filter(item => item.registeredOn >= range.from && item.registeredOn <= range.to)
        const registered = new Map(data.patients.map(item => [item.id, item.registeredOn]))
        const returning = new Set(visits.filter(item => (registered.get(item.patientId) ?? '') < range.from).map(item => item.patientId))

        let revenue = 0
        const methods = new Map<string, number>()
        for (const invoice of data.invoices) {
            for (const payment of invoice.payments) {
                if (payment.date < range.from || payment.date > range.to) continue
                revenue += payment.amount
                methods.set(payment.method, (methods.get(payment.method) ?? 0) + payment.amount)
            }
        }

        const invoicesInRange = data.invoices.filter(item => item.date >= range.from && item.date <= range.to)
        const billed = invoicesInRange.reduce((sum, item) => sum + getInvoiceSummary(item).total, 0)
        const pendingInvoices = data.invoices
            .map(invoice => ({ invoice, summary: getInvoiceSummary(invoice) }))
            .filter(({ invoice, summary }) => summary.balance > 0 && invoice.date >= range.from && invoice.date <= range.to)
            .sort((a, b) => b.summary.balance - a.summary.balance)

        const treatmentRows = new Map<string, { name: string, visits: number, patients: Set<string>, revenue: number }>()
        for (const visit of visits) {
            for (const ref of visit.treatments) {
                const row = treatmentRows.get(ref.name) ?? { name: ref.name, visits: 0, patients: new Set<string>(), revenue: 0 }
                row.visits += 1
                row.patients.add(visit.patientId)
                treatmentRows.set(ref.name, row)
            }
        }
        for (const invoice of invoicesInRange) {
            for (const item of invoice.items) {
                const row = treatmentRows.get(item.description) ?? { name: item.description, visits: 0, patients: new Set<string>(), revenue: 0 }
                row.revenue += item.unitPrice * item.quantity
                treatmentRows.set(item.description, row)
            }
        }

        return {
            visits,
            uniquePatients: uniquePatients.size,
            newPatients: newPatients.length,
            returning: returning.size,
            revenue,
            billed,
            methods: Array.from(methods, ([method, value]) => ({ name: PAYMENT_METHOD_LABELS[method as keyof typeof PAYMENT_METHOD_LABELS] ?? method, value })).sort((a, b) => b.value - a.value),
            pendingInvoices,
            pendingTotal: pendingInvoices.reduce((sum, item) => sum + item.summary.balance, 0),
            treatments: Array.from(treatmentRows.values()).sort((a, b) => b.visits - a.visits || b.revenue - a.revenue),
            followUps: getFollowUpCompletion(data.followUps, range.from, range.to, today)
        }
    }, [data, range, today])

    if (!ready) {
        return (
            <>
                <Seo title='Reports' />
                <PageLoading variant='dashboard' />
            </>
        )
    }

    const monthKeys = getMonthKeysBetween(range.from, range.to)
    const visitGranularity = visitView === 'auto' ? (length > 92 ? 'monthly' : length > 31 ? 'weekly' : 'daily') : visitView
    const revenueGranularity = revenueView === 'auto' ? (length > 62 ? 'monthly' : 'daily') : revenueView

    const visitPoints = visitGranularity === 'daily'
        ? getDailyVisits(data.visits, range.from, range.to).map(point => ({ label: point.label, value: point.value }))
        : visitGranularity === 'weekly'
            ? getWeeklyVisits(data.visits, range.from, range.to).map(point => ({ label: point.label, value: point.value, title: `Week of ${point.label}` }))
            : getMonthlyVisits(report.visits, monthKeys).map(point => ({ label: point.label, value: point.value }))

    const newReturning = getNewVsReturning(
        data.patients.filter(item => item.registeredOn <= range.to),
        report.visits,
        monthKeys
    ).map(point => ({ label: point.label, newPatients: point.newPatients, returning: point.returning }))

    const revenuePoints = revenueGranularity === 'daily'
        ? getDailyRevenue(data.invoices, range.from, range.to).map(point => ({ label: point.label, value: point.value }))
        : getMonthlyRevenue(
            data.invoices.map(invoice => ({ ...invoice, payments: invoice.payments.filter(payment => payment.date >= range.from && payment.date <= range.to) })),
            monthKeys
        ).map(point => ({ label: point.label, value: point.value }))

    const rangeText = `${formatDate(range.from)} to ${formatDate(range.to)}`

    return (
        <>
            <Seo title='Reports' description='Patient visits, revenue, treatments, pending payments and follow-up completion.' />

            <PageHeader
                title='Reports & analytics'
                description={`Showing ${rangeText} (${pluralize(length, 'day')}).`}
                actions={(
                    <Button variant='outline' onClick={() => window.print()}>
                        <Printer aria-hidden='true' />
                        Print report
                    </Button>
                )}
            />

            <div className='mb-6 hidden border-b-2 border-[#0b7f7a] pb-3 print:block'>
                <p className='text-lg font-extrabold tracking-wide text-[#0b7f7a] uppercase'>{data.settings.clinic.name}</p>
                <p className='text-sm font-semibold'>Clinic report: {rangeText}</p>
                <p className='text-xs text-slate-500'>Printed {formatDateTime(new Date().toISOString())}</p>
            </div>

            <Card className='mb-6 print:hidden' size='sm'>
                <CardContent className='flex flex-wrap items-center gap-3'>
                    <span className='text-sm font-medium'>Date range</span>
                    <SimpleSelect value={preset} onValueChange={value => setPreset(value as Preset)} options={PRESETS} ariaLabel='Date range' className='w-48' />
                    {preset === 'custom' && (
                        <>
                            <Input type='date' value={customFrom} max={customTo || today} onChange={event => setCustomFrom(event.target.value)} aria-label='From date' className='w-auto' />
                            <span className='text-sm text-muted-foreground'>to</span>
                            <Input type='date' value={customTo} min={customFrom || undefined} max={today} onChange={event => setCustomTo(event.target.value)} aria-label='To date' className='w-auto' />
                        </>
                    )}
                </CardContent>
            </Card>

            <section aria-label='Report summary' className='grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 print:grid-cols-3'>
                <StatCard label='Patient visits' value={formatNumber(report.visits.length)} icon={ClipboardList} tone='teal' hint={`${report.uniquePatients} different patients`} />
                <StatCard label='New patients' value={formatNumber(report.newPatients)} icon={UserPlus} tone='blue' hint='Registered in this period' />
                <StatCard label='Returning patients' value={formatNumber(report.returning)} icon={Users} tone='green' hint='Visited again in this period' />
                <StatCard label='Revenue collected' value={money(report.revenue)} icon={Wallet} tone='green' hint={`${money(report.billed)} billed`} />
                <StatCard label='Pending payments' value={money(report.pendingTotal)} icon={HandCoins} tone='amber' hint={`${pluralize(report.pendingInvoices.length, 'bill')} from this period`} />
                <StatCard label='Follow-up completion' value={formatPercent(report.followUps.rate)} icon={BellRing} tone='blue' hint={`${report.followUps.completed} of ${report.followUps.due} due`} />
            </section>

            <div className='mt-6 grid gap-6 lg:grid-cols-2 print:block'>
                <Card className='print-avoid-break print:mb-4'>
                    <CardHeader>
                        <CardTitle>Patient visits</CardTitle>
                        <CardDescription>{formatNumber(report.visits.length)} visits, grouped {visitGranularity === 'daily' ? 'by day' : visitGranularity === 'weekly' ? 'by week' : 'by month'}</CardDescription>
                        <CardAction className='print:hidden'>
                            <Tabs value={visitGranularity} onValueChange={value => setVisitView(value as 'daily' | 'weekly' | 'monthly')}>
                                <TabsList>
                                    <TabsTrigger value='daily' className='px-2'>Daily</TabsTrigger>
                                    <TabsTrigger value='weekly' className='px-2'>Weekly</TabsTrigger>
                                    <TabsTrigger value='monthly' className='px-2'>Monthly</TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </CardAction>
                    </CardHeader>
                    <CardContent>
                        <ColumnChart
                            data={visitPoints}
                            xKey='label'
                            series={[{ key: 'value', label: 'Visits', color: 'var(--chart-1)' }]}
                            ariaLabel={`Patient visits ${visitGranularity}`}
                            valueFormatter={formatNumber}
                            tooltipLabelKey={visitGranularity === 'weekly' ? 'title' : undefined}
                        />
                    </CardContent>
                </Card>

                <Card className='print-avoid-break print:mb-4'>
                    <CardHeader>
                        <CardTitle>New vs returning patients</CardTitle>
                        <CardDescription>{report.newPatients} new and {report.returning} returning in this period</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ColumnChart
                            data={newReturning}
                            xKey='label'
                            series={[
                                { key: 'newPatients', label: 'New patients', color: 'var(--chart-2)' },
                                { key: 'returning', label: 'Returning patients', color: 'var(--chart-3)' }
                            ]}
                            ariaLabel='New and returning patients per month'
                            valueFormatter={formatNumber}
                            height={236}
                        />
                    </CardContent>
                </Card>

                <Card className='print-avoid-break print:mb-4'>
                    <CardHeader>
                        <CardTitle>Revenue</CardTitle>
                        <CardDescription>{money(report.revenue)} collected {revenueGranularity === 'daily' ? 'per day' : 'per month'}</CardDescription>
                        <CardAction className='print:hidden'>
                            <Tabs value={revenueGranularity} onValueChange={value => setRevenueView(value as 'daily' | 'monthly')}>
                                <TabsList>
                                    <TabsTrigger value='daily' className='px-2'>Daily</TabsTrigger>
                                    <TabsTrigger value='monthly' className='px-2'>Monthly</TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </CardAction>
                    </CardHeader>
                    <CardContent>
                        {revenueGranularity === 'daily'
                            ? (
                                <TrendChart
                                    data={revenuePoints}
                                    xKey='label'
                                    yKey='value'
                                    label='Revenue'
                                    ariaLabel='Revenue collected per day'
                                    valueFormatter={money}
                                    tickFormatter={value => formatCompactCurrency(value, currency)}
                                />
                            )
                            : (
                                <ColumnChart
                                    data={revenuePoints}
                                    xKey='label'
                                    series={[{ key: 'value', label: 'Revenue', color: 'var(--chart-1)' }]}
                                    ariaLabel='Revenue collected per month'
                                    valueFormatter={money}
                                    tickFormatter={value => formatCompactCurrency(value, currency)}
                                />
                            )}
                    </CardContent>
                </Card>

                <Card className='print-avoid-break print:mb-4'>
                    <CardHeader>
                        <CardTitle>Payment methods</CardTitle>
                        <CardDescription>How patients paid in this period</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {report.methods.length
                            ? <BarList items={report.methods} valueFormatter={money} color='var(--chart-2)' ariaLabel='Revenue by payment method' />
                            : <EmptyState icon={Wallet} title='No payments in this period' compact />}

                        <div className='mt-6 rounded-lg border p-4'>
                            <div className='flex items-center justify-between gap-3'>
                                <div>
                                    <p className='text-sm font-semibold'>Follow-up completion rate</p>
                                    <p className='text-xs text-muted-foreground'>{report.followUps.completed} completed, {report.followUps.pending} still pending of {report.followUps.due} due</p>
                                </div>
                                <p className='text-2xl font-semibold'>{formatPercent(report.followUps.rate)}</p>
                            </div>
                            <Progress value={Math.round(report.followUps.rate)} className='mt-3' aria-label='Follow-up completion rate' />
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card className='mt-6 gap-0 py-0 print-avoid-break print:mb-4'>
                <CardHeader className='border-b py-4'>
                    <CardTitle>Treatment-wise patient count</CardTitle>
                    <CardDescription>Visits, patients and billed amount per treatment in this period</CardDescription>
                </CardHeader>
                {report.treatments.length === 0
                    ? <EmptyState icon={ClipboardList} title='No treatments in this period' compact />
                    : (
                        <div className='grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] print:block'>
                            <BarList items={report.treatments.filter(item => item.visits > 0).slice(0, 8).map(item => ({ name: item.name, value: item.visits }))} valueFormatter={formatNumber} ariaLabel='Visits per treatment' />
                            <Table className='print:mt-4'>
                                <TableHeader>
                                    <TableRow className='hover:bg-transparent'>
                                        <TableHead className={headClass}>Treatment</TableHead>
                                        <TableHead className={`${headClass} text-right`}>Visits</TableHead>
                                        <TableHead className={`${headClass} text-right`}>Patients</TableHead>
                                        <TableHead className={`${headClass} text-right`}>Billed</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {report.treatments.map(item => (
                                        <TableRow key={item.name}>
                                            <TableCell className='max-w-[14rem] truncate font-medium'>{item.name}</TableCell>
                                            <TableCell className='text-right tabular-nums'>{item.visits}</TableCell>
                                            <TableCell className='text-right tabular-nums'>{item.patients.size}</TableCell>
                                            <TableCell className='text-right tabular-nums'>{money(item.revenue)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
            </Card>

            <Card className='mt-6 gap-0 py-0 print-avoid-break'>
                <CardHeader className='border-b py-4'>
                    <CardTitle>Pending payments</CardTitle>
                    <CardDescription>Bills from this period that still have a balance, largest first</CardDescription>
                </CardHeader>
                {report.pendingInvoices.length === 0
                    ? <EmptyState icon={HandCoins} title='Everything is paid' description='No bills from this period have a balance.' compact />
                    : (
                        <Table>
                            <TableHeader>
                                <TableRow className='hover:bg-transparent'>
                                    <TableHead className={`${headClass} pl-5`}>Invoice</TableHead>
                                    <TableHead className={headClass}>Patient</TableHead>
                                    <TableHead className={`${headClass} hidden sm:table-cell`}>Date</TableHead>
                                    <TableHead className={`${headClass} text-right`}>Total</TableHead>
                                    <TableHead className={`${headClass} text-right`}>Balance</TableHead>
                                    <TableHead className={`${headClass} hidden md:table-cell`}>Outstanding for</TableHead>
                                    <TableHead className={`${headClass} pr-5`}>Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {report.pendingInvoices.map(({ invoice, summary }) => (
                                    <TableRow key={invoice.id}>
                                        <TableCell className='pl-5'>
                                            <Link href={`/billing/${invoice.id}`} className='font-mono text-xs font-medium hover:text-primary hover:underline'>{invoice.number}</Link>
                                        </TableCell>
                                        <TableCell className='max-w-[12rem] truncate'>{patientsById.get(invoice.patientId)?.name ?? 'Removed patient'}</TableCell>
                                        <TableCell className='hidden text-muted-foreground sm:table-cell'>{formatDate(invoice.date)}</TableCell>
                                        <TableCell className='text-right tabular-nums'>{money(summary.total)}</TableCell>
                                        <TableCell className='text-right font-semibold text-rose-700 tabular-nums'>{money(summary.balance)}</TableCell>
                                        <TableCell className='hidden text-muted-foreground md:table-cell'>{pluralize(Math.max(daysBetween(invoice.date, today), 0), 'day')}</TableCell>
                                        <TableCell className='pr-5'><PaymentStatusBadge status={summary.status} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
            </Card>
        </>
    )
}

export default ReportsPage
